class SocialGrid extends HTMLElement {
  constructor() {
    super();

    this.isV2 = this.classList.contains('social-grid--v2');
    this.header = this.querySelector('.social-grid__header, .social-grid__subheader');
    this.btn = this.querySelector('.social-grid__column--text .mt-btn');

    if (this.isV2) {
      this.track = this.querySelector('.marquee-track');
      const autoplayElement = this.querySelector('.marquee-container');
      this.autoplay = autoplayElement?.dataset?.autoplay === 'true';

      this.animate = this.animate.bind(this);
      this.dragStart = this.dragStart.bind(this);
      this.dragMove = this.dragMove.bind(this);
      this.dragEnd = this.dragEnd.bind(this);
    } else {
      this.swiperRow1 = this.querySelector('.swiper-row-1');
      this.swiperRow2 = this.querySelector('.swiper-row-2');
      this.autoplay = this.swiperRow1?.dataset?.autoplay === 'true';
    }
  }

  connectedCallback() {
    this.initializeLayout();
  }

  disconnectedCallback() {
    if (this.isV2) {
      this.removeMarqueeEventListeners();
      if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    } else if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }

  initializeLayout() {
    if (this.isV2) {
      this.initializeMarquee();
    } else {
      this.initializeSwipersV1();
    }

    this.attachListeners();
    this.initializeLazyLoading();
  }

  attachListeners() {
    if (!this.btn || !this.header) return;
    this.btn.addEventListener('click', () => {
      window.app.dl.ctaClick(this.btn.dataset.text, { location: this.header.dataset.text || '' });
    });
  }

  initializeLazyLoading() {
    const lazyVideos = this.querySelectorAll('video[data-src]');
    if (!lazyVideos.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const video = entry.target;
          video.src = video.dataset.src;
          video.load();
          video.play().catch(() => {});
          video.classList.remove('lazy');
          obs.unobserve(video);
        });
      });

      lazyVideos.forEach(video => observer.observe(video));
    } else {
      lazyVideos.forEach(video => {
        video.src = video.dataset.src;
        video.load();
        video.play().catch(() => {});
      });
    }
  }

  initializeMarquee() {
    if (!this.track || !this.track.children.length) return;

    const firstChild = this.track.children[0];
    const clone = firstChild.cloneNode(true);
    this.track.appendChild(clone);

    this.position = 0;
    this.speed = this.autoplay ? -0.5 : 0;
    this.isDragging = false;
    this.startPos = 0;
    this.trackWidth = firstChild.offsetWidth + parseInt(getComputedStyle(firstChild).marginRight || 0);
    this.animationFrameId = null;

    if (this.speed !== 0) this.addMarqueeEventListeners();
  }

  addMarqueeEventListeners() {
    this.track.addEventListener('mousedown', this.dragStart);
    this.track.addEventListener('touchstart', this.dragStart, { passive: false });

    window.addEventListener('mousemove', this.dragMove);
    window.addEventListener('touchmove', this.dragMove, { passive: false });

    window.addEventListener('mouseup', this.dragEnd);
    window.addEventListener('touchend', this.dragEnd);

    this.animate();
  }

  removeMarqueeEventListeners() {
    if (!this.track) return;

    this.track.removeEventListener('mousedown', this.dragStart);
    this.track.removeEventListener('touchstart', this.dragStart);

    window.removeEventListener('mousemove', this.dragMove);
    window.removeEventListener('touchmove', this.dragMove);

    window.removeEventListener('mouseup', this.dragEnd);
    window.removeEventListener('touchend', this.dragEnd);
  }

  animate() {
    if (!this.isDragging) {
      this.position += this.speed;
      if (this.position < -this.trackWidth) this.position += this.trackWidth;
      if (this.position > 0) this.position -= this.trackWidth;
    }

    this.track.style.transform = `translateX(${this.position}px)`;
    this.animationFrameId = requestAnimationFrame(this.animate);
  }

  dragStart(e) {
    if (this.speed === 0) return;

    this.isDragging = true;
    this.track.classList.add('grabbing');
    this.startPos = (e.pageX || e.touches[0].pageX) - this.position;
    this.startPosY = e.pageY || e.touches[0].pageY;

    cancelAnimationFrame(this.animationFrameId);
  }

  dragMove(e) {
    if (!this.isDragging) return;

    const currentX = e.pageX || e.touches[0].pageX;
    const currentY = e.pageY || e.touches[0].pageY;

    const deltaX = Math.abs(currentX - (this.startPos + this.position));
    const deltaY = Math.abs(currentY - this.startPosY);
    if (deltaX > deltaY) e.preventDefault();

    this.position = currentX - this.startPos;
    this.track.style.transform = `translateX(${this.position}px)`;
  }

  dragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.classList.remove('grabbing');
    cancelAnimationFrame(this.animationFrameId);
    this.animate();
  }

  initializeSwipersV1() {
    if (!this.swiperRow1) return;
    if (typeof Swiper === 'undefined') {
      console.error('Swiper is not defined. Make sure it is loaded before this component.');
      return;
    }

    this.swiper = new Swiper(this.swiperRow1, {
      slidesPerView: 1,
      spaceBetween: -4,
      loop: false,
      speed: 1000,
      allowTouchMove: true,
      autoplay: this.autoplay
        ? { delay: 6000, disableOnInteraction: false }
        : false,
      centeredSlides: true,
      breakpoints: {
        450: { spaceBetween: -8 },
        550: { spaceBetween: -12 },
        700: { spaceBetween: -16 },
        992: { spaceBetween: 0 }
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true
      }
    });
  }
}

customElements.define('social-grid', SocialGrid);